import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { string, number, shape, func, arrayOf, oneOf } from 'prop-types';
import { Trash2 as DeleteIcon } from 'react-feather';
import { Link } from 'react-router-dom';
import configuredVariant from '@magento/peregrine/lib/util/configuredVariant';
import ProductOptions from '@magento/venia-ui/lib/components/LegacyMiniCart/productOptions';
import Price from '@magento/venia-ui/lib/components/Price';
import { useItem } from '@magento/peregrine/lib/talons/MiniCart/useItem';
import resourceUrl from '@magento/peregrine/lib/util/makeUrl';
import { useQuery } from '@apollo/client';
import { Image, CloseIcon } from 'src/components';
import defaultClasses from './item.module.css';
import { useMergeCssClasses } from '../../../../../Utils/utils';
import { GET_PARENT_PRODUCT_DATA } from '../../../../peregrine/talons/CartPage/ProductListing/product.gql';

var extractOutletSortNumber = function extractOutletSortNumber(url) {
  var regex = /\/([^\/_]*_(\d+))/;
  var result = url.match(regex);

  if (result) {
    return parseInt(result[2]);
  } else {
    // console.log("No number preceded by underscore was found in the URL after the last '/'");
    return 1000;
  }
};

var extractPrimaryOutletImage = function extractPrimaryOutletImage(media_gallery) {
  var _media_gallery$, _media_gallery$2;

  if (media_gallery && media_gallery.length < 1) return null;
  var primaryImage = (_media_gallery$ = media_gallery[0]) === null || _media_gallery$ === void 0 ? void 0 : _media_gallery$.url;
  var minSortNumber = extractOutletSortNumber((_media_gallery$2 = media_gallery[0]) === null || _media_gallery$2 === void 0 ? void 0 : _media_gallery$2.url);
  media_gallery === null || media_gallery === void 0 ? void 0 : media_gallery.forEach(function (item) {
    var _media_gallery$3;

    var currentSortNumber = extractOutletSortNumber((_media_gallery$3 = media_gallery[0]) === null || _media_gallery$3 === void 0 ? void 0 : _media_gallery$3.url);

    if (currentSortNumber < minSortNumber) {
      minSortNumber = currentSortNumber;
      primaryImage = item === null || item === void 0 ? void 0 : item.url;
    }
  });
  return primaryImage;
};

var Item = function Item(props) {
  var _product$sku, _product$sku2, _product$sku3, _product$url_key$matc, _product$url_key, _product$url_key$matc2, _product$price_range, _outletPriceRange$reg2, _outletPriceRange$fin2, _product$sku4, _product$sku5, _outletPriceRange$fin3, _outletPriceRange$fin4, _outletPriceRange$reg3, _outletPriceRange$reg4;

  var propClasses = props.classes,
      product = props.product,
      uid = props.uid,
      quantity = props.quantity,
      configurable_options = props.configurable_options,
      handleRemoveItem = props.handleRemoveItem,
      prices = props.prices,
      closeMiniCart = props.closeMiniCart,
      configurableThumbnailSource = props.configurableThumbnailSource,
      storeUrlSuffix = props.storeUrlSuffix;
  var simpleSku = (product === null || product === void 0 ? void 0 : product.__typename) == 'SimpleProduct' ? product !== null && product !== void 0 && (_product$sku = product.sku) !== null && _product$sku !== void 0 && _product$sku.startsWith('OUT-') ? product === null || product === void 0 ? void 0 : (_product$sku2 = product.sku) === null || _product$sku2 === void 0 ? void 0 : _product$sku2.match(/OUT\-[^-]+/)[0] : product === null || product === void 0 ? void 0 : (_product$sku3 = product.sku) === null || _product$sku3 === void 0 ? void 0 : _product$sku3.substr(0, product === null || product === void 0 ? void 0 : product.sku.indexOf('-')) : null;

  var _useQuery = useQuery(GET_PARENT_PRODUCT_DATA, {
    fetchPolicy: 'cache-and-network',
    variables: {
      sku: simpleSku
    },
    skip: !simpleSku
  }),
      parentData = _useQuery.data;

  var _useIntl = useIntl(),
      formatMessage = _useIntl.formatMessage;

  var classes = useMergeCssClasses(defaultClasses, propClasses);
  var isOutlet = product === null || product === void 0 ? void 0 : product.sku.startsWith('OUT-');
  var productUrlKey = isOutlet ? (_product$url_key$matc = product === null || product === void 0 ? void 0 : (_product$url_key = product.url_key) === null || _product$url_key === void 0 ? void 0 : (_product$url_key$matc2 = _product$url_key.match(/(.*)-[0-9]+$/)) === null || _product$url_key$matc2 === void 0 ? void 0 : _product$url_key$matc2[1]) !== null && _product$url_key$matc !== void 0 ? _product$url_key$matc : false : product === null || product === void 0 ? void 0 : product.url_key;
  var itemLink = useMemo(function () {
    return resourceUrl("/".concat(productUrlKey).concat(storeUrlSuffix || ''));
  }, [productUrlKey, storeUrlSuffix]);
  var stockStatusText = product && (product === null || product === void 0 ? void 0 : product.stock_status) === 'OUT_OF_STOCK' ? formatMessage({
    id: 'productList.outOfStock',
    defaultMessage: 'Out-of-stock'
  }) : '';

  var _useItem = useItem({
    uid: uid,
    handleRemoveItem: handleRemoveItem
  }),
      isDeleting = _useItem.isDeleting,
      removeItem = _useItem.removeItem;

  var rootClass = isDeleting ? classes.root_disabled : classes.root;
  var configured_variant = configuredVariant(configurable_options, product);
  var outletPriceRange = isOutlet ? product === null || product === void 0 ? void 0 : (_product$price_range = product.price_range) === null || _product$price_range === void 0 ? void 0 : _product$price_range.minimum_price : null;
  var shouldShowDiscount = useMemo(function () {
    var _outletPriceRange$reg, _outletPriceRange$fin;

    return (outletPriceRange === null || outletPriceRange === void 0 ? void 0 : (_outletPriceRange$reg = outletPriceRange.regular_price) === null || _outletPriceRange$reg === void 0 ? void 0 : _outletPriceRange$reg.value) !== (outletPriceRange === null || outletPriceRange === void 0 ? void 0 : (_outletPriceRange$fin = outletPriceRange.final_price) === null || _outletPriceRange$fin === void 0 ? void 0 : _outletPriceRange$fin.value);
  }, [outletPriceRange === null || outletPriceRange === void 0 ? void 0 : (_outletPriceRange$reg2 = outletPriceRange.regular_price) === null || _outletPriceRange$reg2 === void 0 ? void 0 : _outletPriceRange$reg2.value, outletPriceRange === null || outletPriceRange === void 0 ? void 0 : (_outletPriceRange$fin2 = outletPriceRange.final_price) === null || _outletPriceRange$fin2 === void 0 ? void 0 : _outletPriceRange$fin2.value]);
  var productImage = configurableThumbnailSource === 'itself' && configured_variant ? configured_variant.thumbnail.url : product.thumbnail.url;
  var outletImage = productImage && !(productImage !== null && productImage !== void 0 && productImage.includes('placeholder')) && product !== null && product !== void 0 && (_product$sku4 = product.sku) !== null && _product$sku4 !== void 0 && _product$sku4.startsWith('OUT-') ? productImage === null || productImage === void 0 ? void 0 : productImage.url : extractPrimaryOutletImage(product === null || product === void 0 ? void 0 : product.media_gallery);
  var image = productImage !== null && productImage !== void 0 && productImage.includes('placeholder') && product !== null && product !== void 0 && (_product$sku5 = product.sku) !== null && _product$sku5 !== void 0 && _product$sku5.startsWith('OUT-') ? outletImage : productImage;
  return React.createElement("div", {
    className: rootClass
  }, React.createElement(Link, {
    className: classes.thumbnailContainer,
    to: itemLink,
    onClick: closeMiniCart,
    "aria-label": "Go to ".concat(product.name, " page")
  }, React.createElement(Image, {
    alt: product.name,
    classes: {
      root: classes.thumbnail
    },
    width: 100,
    resource: image
  })), React.createElement(Link, {
    className: classes.name,
    to: itemLink,
    onClick: closeMiniCart,
    "aria-label": "Go to ".concat(product.name, " page")
  }, product.name), React.createElement("span", {
    className: classes.quantity
  }, React.createElement("div", {
    className: "flex items-center mr-16"
  }, React.createElement(FormattedMessage, {
    id: 'productList.quantityText',
    defaultMessage: 'Qty'
  }), React.createElement("div", {
    className: "border border-solid border-gray py-1 px-7 ml-3 leading-171"
  }, quantity)), isOutlet && outletPriceRange ? React.createElement(React.Fragment, null, React.createElement("span", {
    className: classes.price
  }, React.createElement(Price, {
    currencyCode: outletPriceRange === null || outletPriceRange === void 0 ? void 0 : (_outletPriceRange$fin3 = outletPriceRange.final_price) === null || _outletPriceRange$fin3 === void 0 ? void 0 : _outletPriceRange$fin3.currency,
    value: outletPriceRange === null || outletPriceRange === void 0 ? void 0 : (_outletPriceRange$fin4 = outletPriceRange.final_price) === null || _outletPriceRange$fin4 === void 0 ? void 0 : _outletPriceRange$fin4.value,
    classes: {
      root: "font-bold ".concat(shouldShowDiscount && 'text-red', " mr-2")
    }
  })), shouldShowDiscount ? React.createElement("span", {
    className: classes.price
  }, React.createElement(Price, {
    currencyCode: outletPriceRange === null || outletPriceRange === void 0 ? void 0 : (_outletPriceRange$reg3 = outletPriceRange.regular_price) === null || _outletPriceRange$reg3 === void 0 ? void 0 : _outletPriceRange$reg3.currency,
    value: outletPriceRange === null || outletPriceRange === void 0 ? void 0 : (_outletPriceRange$reg4 = outletPriceRange.regular_price) === null || _outletPriceRange$reg4 === void 0 ? void 0 : _outletPriceRange$reg4.value,
    classes: {
      root: 'font-bold text-black line-through '
    }
  })) : null) : React.createElement("span", {
    className: classes.price
  }, React.createElement(Price, {
    currencyCode: prices.price.currency,
    value: prices.price.value,
    classes: {
      root: 'font-bold text-black '
    }
  }))), React.createElement("button", {
    onClick: removeItem,
    type: "button",
    className: classes.deleteButton,
    disabled: isDeleting,
    "aria-label": "Delete ".concat(product.name, " from cart")
  }, React.createElement("span", {
    className: "sr-only"
  }, "Delete ".concat(product.name, " from cart")), React.createElement(CloseIcon, null)));
};

export default Item;
Item.propTypes = {
  classes: shape({
    root: string,
    thumbnail: string,
    name: string,
    options: string,
    quantity: string,
    price: string,
    editButton: string,
    editIcon: string
  }),
  product: shape({
    name: string,
    thumbnail: shape({
      url: string
    })
  }),
  id: string,
  quantity: number,
  configurable_options: arrayOf(shape({
    id: number,
    option_label: string,
    value_id: number,
    value_label: string
  })),
  handleRemoveItem: func,
  prices: shape({
    price: shape({
      value: number,
      currency: string
    })
  }),
  configured_variant: shape({
    thumbnail: shape({
      url: string
    })
  }),
  configurableThumbnailSource: oneOf(['parent', 'itself'])
};